import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icons';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import { PlusSign } from '@/components/Icons/PlusSign';
import {
  BorderRadius,
  ButtonVariant,
  IconType,
  Size,
} from '@/types/components';
import { PersonType, type FamilyMember } from '@/types/user';
import { getRoleLabel } from '@/utils/person';

interface SectionProps {
  icon: IconType;
  title: string;
  muted?: boolean;
  onClick?: () => void;
}

const Section: React.FC<SectionProps> = ({ icon, title, muted, onClick }) => {
  const content = (
    <>
      <Icon icon={icon} className="size-4 shrink-0 text-primary" />
      <span
        className={`min-w-0 flex-1 truncate text-left text-[16px]/[20px] md:text-[16px]/[24px] ${muted ? 'text-subtle-foreground' : 'text-foreground'}`}
      >
        {title}
      </span>
      {onClick && (
        <ChevronRight
          strokeWidth="2"
          className="size-3.5 shrink-0 text-muted-foreground"
        />
      )}
    </>
  );

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-3 px-4 py-4 transition-colors hover:bg-hover"
    >
      {content}
    </button>
  ) : (
    <div className="flex items-center gap-3 px-4 py-4">{content}</div>
  );
};

interface Props {
  personType: PersonType;
  member?: FamilyMember;
  onAdd: () => void;
  onPhone: (member: FamilyMember) => void;
  onNotifications: (member: FamilyMember) => void;
}

export const FamilyMemberCard: React.FC<Props> = ({
  personType,
  member,
  onAdd,
  onPhone,
  onNotifications,
}) => {
  const { t } = useTranslation();

  const roleLabel = getRoleLabel(personType);
  const phoneNumber = member?.phoneNumber;
  const email = member?.emailAddress;
  const userPublicId = member?.userPublicId;
  const hasMember = !!userPublicId || !!phoneNumber || !!email;

  const divider = <div className="mx-4 h-px bg-divider" />;

  return (
    <div className="overflow-hidden rounded-xl bg-card shadow-card">
      <div
        className={`flex items-center gap-3 px-4 pt-4 ${hasMember ? 'pb-1' : 'pb-4'}`}
      >
        <p className="min-w-0 flex-1 text-[16px]/[20px] font-medium text-foreground md:text-[16px]/[24px]">
          {roleLabel}
        </p>
        {!hasMember && (
          <Button
            variant={ButtonVariant.Primary}
            size={Size.Small}
            radius={BorderRadius.Pill}
            className="shrink-0 gap-1.5 pr-4 pl-3 text-subhead-14 font-semibold"
            onClick={onAdd}
          >
            <PlusSign strokeWidth="1.8" className="size-4 shrink-0" />
            <span>{t('familySettings.add')}</span>
          </Button>
        )}
      </div>

      {hasMember && member && (
        <>
          {phoneNumber && userPublicId && (
            <>
              <Section
                icon={IconType.Phone}
                title={phoneNumber}
                onClick={() => onPhone(member)}
              />
              {divider}
            </>
          )}
          <Section
            icon={IconType.BellRinging}
            title={t('familySettings.notifications')}
            onClick={userPublicId ? () => onNotifications(member) : undefined}
          />
          {email && (
            <>
              {divider}
              <Section icon={IconType.Mail} title={email} muted />
            </>
          )}
        </>
      )}
    </div>
  );
};
